import { z } from "zod";

export const TRACK_EVENT_TYPES = ["page_view", "user_action", "error"] as const;
export type TrackEventType = (typeof TRACK_EVENT_TYPES)[number];

const trackEventSchema = z.object({
  type: z.enum(TRACK_EVENT_TYPES),
  name: z.string().min(1, "O evento de tracking precisa de um nome"),
  timestamp: z.string(),
  metadata: z.record(z.unknown()).optional(),
});

export type ITrackEvent = z.infer<typeof trackEventSchema>;
export type ITrackEventInput = Omit<ITrackEvent, "timestamp">;

/**
 * Envia o evento já validado para o backend de tracking.
 *
 * O endpoint ainda não existe — por enquanto só loga no console de forma
 * estruturada. Quando o endpoint estiver pronto, troque o console.log pela
 * chamada real, por exemplo:
 *
 *   return ApiBrave.post("/tracking/eventos", event).catch((error) =>
 *     console.warn("[Tracking] falha ao enviar evento:", error)
 *   );
 *
 * Mantenha essa função como o único lugar que efetivamente envia o evento —
 * assim a troca por uma chamada real fica isolada aqui, sem precisar tocar
 * em nenhum dos pontos de chamada espalhados pelo app.
 */
function sendEvent(event: ITrackEvent): void {
  // eslint-disable-next-line no-console
  console.log("[Tracking]", event);
}

/**
 * Registra um evento de tracking (visita de página, ação do usuário ou
 * erro). Nunca lança exceção — uma falha aqui não pode derrubar o fluxo
 * real da aplicação, já que é chamada em lugares como catch de requisição
 * e handlers de clique.
 */
function track(input: ITrackEventInput): void {
  try {
    const result = trackEventSchema.safeParse({
      ...input,
      timestamp: new Date().toISOString(),
    });

    if (!result.success) {
      console.warn(
        "[Tracking] payload inválido, evento descartado:",
        result.error.flatten()
      );
      return;
    }

    sendEvent(result.data);
  } catch (error) {
    console.warn("[Tracking] falha inesperada ao registrar evento:", error);
  }
}

export class Tracking {
  /** Uso genérico — prefira pageView/userAction/error quando possível. */
  static track = track;

  /** O usuário acessou uma página. Chame dentro de um useEffect de montagem. */
  static pageView(name: string, metadata?: Record<string, unknown>): void {
    track({ type: "page_view", name, metadata });
  }

  /** O usuário realizou uma ação (clique de botão, submit, etc). */
  static userAction(name: string, metadata?: Record<string, unknown>): void {
    track({ type: "user_action", name, metadata });
  }

  /** Aconteceu um erro (requisição, validação, exceção). */
  static error(name: string, metadata?: Record<string, unknown>): void {
    track({ type: "error", name, metadata });
  }
}
